import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DATE_FORMAT } from 'src/app/config/global.config';
import { AdaptToUserUtcOffsetPipe } from 'src/app/core/pipes/adapt-to-user-utc-offset';
import { ILocation, IPayment_term } from 'src/app/features/customers/models/customers.models';
import { CustomFieldTypes } from 'src/app/features/settings/models/custom-fields.constants';
import { ICustomField, IObjectCustomFields } from 'src/app/features/settings/models/settings.models';
import { IDetailField } from 'src/app/ui/detail-field/detail-field.models';
import {
  IDetailFieldGroup,
  IDetailFieldGroupChange,
} from 'src/app/ui/detail-fields-settings/detail-fields-settings.component';
import {
  ITableColumnSetting,
  TablePreferencesService,
} from 'src/app/shared/table-preferences.service';
import { IOrderCompany, ISegregationInfo, ISegregationSplit } from '../models/quotations.models';

/** Everything the field definitions need from whichever surface is rendering. */
export interface IOrderDetailFieldsContext {
  location?: ILocation | null;
  /**
   * The actual delivery location (WB-7836), distinct from `location` (the
   * billing/customer location) — used for the Shipping block's own erp_id
   * so it never shows the Customer's data instead.
   */
  shippingLocation?: ILocation | null;
  orderCompany?: IOrderCompany | null;
  paymentTerms?: IPayment_term | null;
  paymentTermOptions?: IPayment_term[];
  customerPoNumber?: string | null;
  quoteId?: string | null;
  shippingDate?: string | null;
  comment?: string | null;
  objectCustomFields?: IObjectCustomFields | null;
  /** Language used to resolve custom-field labels. */
  currentLanguage?: string;
  /** Order fields the current role hides (the `*appFieldVisible` rule). */
  hiddenOrderFields?: string[];
  /** True when a free-shipping price-book rule applies to this order. */
  hasFreeShipping?: boolean;
}

/** The blocks, plus the visible rows of each, ready to render. */
export interface IOrderDetailFields {
  groups: IDetailFieldGroup[];
  customer: IDetailField[];
  shipping: IDetailField[];
  references: IDetailField[];
  customFields: IDetailField[];
}

/**
 * Single source of truth for the configurable blocks of an order's detail view
 * (WB-7463).
 *
 * Both surfaces that show these blocks — the order detail page's right panel
 * and the side-by-side correction panel — build them from here, so the sections,
 * their order, the field keys and the storage keys are identical. Because the
 * preference keys are shared, hiding or reordering a field on one surface shows
 * up on the other; that is the point, not a side effect.
 *
 * The service is stateless: callers pass their current values in and render what
 * comes back.
 */
@Injectable({ providedIn: 'root' })
export class OrderDetailFieldsService {
  /** Block definitions, in display order. */
  static readonly GROUPS = [
    {
      key: 'customer',
      title: 'quotations_and_orders.customer',
      prefsKey: 'order_detail_customer_fields',
    },
    {
      key: 'shipping',
      title: 'companies.shipping',
      prefsKey: 'order_detail_shipping_fields',
    },
    {
      key: 'references',
      title: 'quotations_and_orders.order_references',
      prefsKey: 'order_detail_reference_fields',
    },
    {
      key: 'custom_fields',
      title: 'custom_fields.custom_fields',
      prefsKey: 'order_detail_custom_fields',
    },
  ] as const;

  constructor(
    private readonly tablePreferences: TablePreferencesService,
    private readonly translate: TranslateService
  ) {}

  /**
   * Build every block from `context`, then apply the user's saved visibility and
   * order on top. Cheap and idempotent — call it on any change that touches the
   * displayed values.
   */
  build(context: IOrderDetailFieldsContext): IOrderDetailFields {
    const definitions: Record<string, IDetailField[]> = {
      customer: this.defineCustomerFields(context),
      shipping: this.defineShippingFields(context),
      references: this.defineReferenceFields(context),
      custom_fields: this.defineCustomFields(context),
    };

    const visible: Record<string, IDetailField[]> = {};
    const groups = OrderDetailFieldsService.GROUPS.map((group) => {
      // A field this deployment does not support is dropped entirely, including
      // from the configurator — users are never offered an unrenderable row.
      const available = definitions[group.key].filter((field) => field.available !== false);
      const ordered = this.tablePreferences.applyPreferences(
        group.prefsKey,
        available
      ) as IDetailField[];

      // `applyPreferences` clones, so re-attach the live data it dropped.
      const byKey = new Map(available.map((field) => [field.key, field]));
      const hydrated = ordered.map((column) => ({
        ...byKey.get(column.key)!,
        visible: column.visible,
      }));

      visible[group.key] = hydrated.filter((field) => field.visible);
      return {
        key: group.key,
        title: group.title,
        prefsKey: group.prefsKey,
        fields: hydrated.map(({ key, label, visible: isVisible, pinned, hideable }) => ({
          key,
          label,
          visible: isVisible,
          pinned,
          hideable,
        })),
      };
    });

    return {
      groups,
      customer: visible['customer'],
      shipping: visible['shipping'],
      references: visible['references'],
      customFields: visible['custom_fields'],
    };
  }

  /**
   * Persist one block's new visibility/order. Only the fields the user actually
   * flipped are written, so untouched ones keep falling through to their
   * computed default.
   */
  saveGroup(change: IDetailFieldGroupChange, currentGroups: IDetailFieldGroup[]): void {
    const previous = new Map(
      (currentGroups.find((group) => group.key === change.group.key)?.fields ?? []).map(
        (field) => [field.key, !!field.visible]
      )
    );
    const changedVisibility: Record<string, boolean> = {};
    change.columns.forEach((column: ITableColumnSetting) => {
      if (previous.get(column.key) !== !!column.visible) {
        changedVisibility[column.key] = !!column.visible;
      }
    });

    this.tablePreferences.save(change.group.prefsKey, change.columns, changedVisibility);
  }

  /**
   * Full description of the rule behind a split, shown in the info popover next
   * to the "Split from" / "Split into" rows: which mechanism ran, what it split
   * on, and the value this order matched.
   */
  segregationRuleSummary(info: ISegregationInfo | ISegregationSplit): string {
    const conditionLabels: Record<string, string> = {
      brand: 'quotations_and_orders.segregation.condition.brand',
      category: 'quotations_and_orders.segregation.condition.category',
      product_label: 'quotations_and_orders.segregation.condition.product_label',
      product_custom_field: 'quotations_and_orders.segregation.condition.product_custom_field',
    };

    const conditionKey = info.condition_type ? conditionLabels[info.condition_type] : null;
    const condition = info.condition_type === 'product_custom_field'
      ? info.custom_field_name || this.translate.instant(conditionKey!)
      : conditionKey
        ? this.translate.instant(conditionKey)
        : '—';

    return [
      `${this.translate.instant('quotations_and_orders.segregation.rule')}: ${info.mechanism_name || '—'}`,
      `${this.translate.instant('quotations_and_orders.segregation.split_by')}: ${condition}`,
      `${this.translate.instant('quotations_and_orders.segregation.value')}: ${info.split_label || '—'}`,
    ].join('\n');
  }

  /**
   * Date as shown in a detail row. `formatDate` throws NG0701 on a locale whose data
   * was never registered, and `build()` runs inside the `quotation$` subscriber — an
   * escaping error kills that subscription, so one date can blank the whole order view.
   * Locales are registered in AppModule; this falls back to `en` if one is ever missed.
   */
  formatDetailDate(value: string | null | undefined, language = 'en'): string | null {
    if (!value) return null;
    const adapted = new AdaptToUserUtcOffsetPipe().transform(value);
    if (!adapted) return null;
    try {
      return formatDate(adapted, DATE_FORMAT, language || 'en');
    } catch {
      return formatDate(adapted, DATE_FORMAT, 'en');
    }
  }

  /**
   * Same date as the `yyyy-MM-dd` a native date input round-trips.
   */
  toDateInputValue(value: string | null | undefined): string | null {
    if (!value) return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    return match ? match[0] : null;
  }

  /** True when the role does not hide this order field. */
  private isOrderFieldVisible(context: IOrderDetailFieldsContext, field: string): boolean {
    return !(context.hiddenOrderFields ?? []).includes(field);
  }

  private defineCustomerFields(context: IOrderDetailFieldsContext): IDetailField[] {
    const location = context.location;

    return [
      {
        key: 'company',
        label: 'companies_selection_dialog.step_billing_company',
        visible: true,
        hideable: false,
        pinned: true,
        mode: 'modal',
        slot: 'company',
      },
      {
        key: 'billing_address',
        label: 'companies.billing_address',
        visible: true,
        hideable: false,
        // Company master data, not an order field: shown (and copyable) to
        // confirm the match, edited on the company record itself.
        mode: 'readonly',
        wraps: true,
        value: location?.billing_address || location?.shipping_address || null,
      },
      // Tax codes live on the location, so they are offered on the shipping
      // block only (`location_tax_id`).
      {
        // WB-7836 (review follow-up): reads orderCompany.company_id, not
        // location.company_id -- this is the billing-company block, and
        // parse_order_company resolves company_id with the order's own
        // subsidiary (push_order.py's ERP target), while
        // _parse_location_lightweight's version is the *location's* own
        // company resolution. They agree in the common case, but
        // orderCompany is the correct, unambiguous source for "the
        // customer's ID" regardless of which location happens to be
        // selected for billing.
        key: 'erp_id',
        label: 'companies.erp_id',
        visible: false,
        mode: 'readonly',
        value: context.orderCompany?.company_id || null,
        available: !!context.orderCompany,
      },
      {
        key: 'payment_terms',
        label: 'companies.payment_terms',
        visible: true,
        mode: 'inline',
        inlineType: 'lookup',
        value: context.paymentTerms?.name || null,
        selectedOptionId: context.paymentTerms?.id ?? null,
        options: (context.paymentTermOptions ?? []).map((term) => ({
          id: term.id,
          name: term.name,
        })),
        clearable: true,
        // Payment terms are a choice, not a code — copying them is noise.
        copyValue: null,
      },
      ...this.defineCompanyCustomFields(context),
    ];
  }

  /**
   * Company-level custom fields (e.g. Euroclone's CCCODIPA). They ride on
   * `order_company.custom_fields`, added by WB-7643 — until that lands the array
   * is absent and no rows are offered in the configurator.
   */
  private defineCompanyCustomFields(context: IOrderDetailFieldsContext): IDetailField[] {
    const customFields = (context.orderCompany as any)?.custom_fields as
      | { technical_name: string; label: string; value: string | number | null }[]
      | undefined;

    return (customFields ?? []).map((field) => ({
      key: `company_custom_${field.technical_name}`,
      // Custom-field labels are user-defined, so they are not i18n keys; the
      // translate pipe passes unknown keys through unchanged.
      label: field.label || field.technical_name,
      visible: false,
      mode: 'readonly' as const,
      value: field.value ?? null,
    }));
  }

  private defineShippingFields(context: IOrderDetailFieldsContext): IDetailField[] {
    // WB-7836 (review follow-up): every row in this block is the Shipping
    // block's own data, not the Customer's -- reading context.location here
    // (the billing location) made the tax codes/erp_id/contacts available
    // whenever the *Customer's* location existed, showing the Customer's
    // data next to the Shipping location's address whenever billing and
    // shipping actually differ.
    const location = context.shippingLocation;
    const contact = location?.contacts_information?.[0];

    return [
      {
        key: 'location',
        label: 'quotations_and_orders.location',
        visible: true,
        hideable: false,
        pinned: true,
        mode: 'modal',
        slot: 'location',
      },
      {
        key: 'shipping_address',
        label: 'companies.shipping_address',
        visible: true,
        mode: 'readonly',
        wraps: true,
        slot: 'shipping_address',
      },
      {
        // Key kept from when the row showed the single `tax_id`, so a user who
        // turned it on keeps seeing it.
        key: 'location_tax_id',
        label: 'companies.tax_codes',
        visible: false,
        mode: 'readonly',
        wraps: true,
        slot: 'tax_codes',
        available: !!location,
      },
      {
        key: 'location_erp_id',
        label: 'companies.erp_id',
        visible: false,
        mode: 'readonly',
        value: location?.erp_id || null,
        available: !!location,
      },
      {
        key: 'main_contact',
        label: 'companies.main_contact_Email',
        visible: true,
        mode: 'readonly',
        slot: 'main_contact',
        available: !!contact,
      },
      {
        key: 'contact_email',
        label: 'common.email',
        visible: true,
        mode: 'readonly',
        slot: 'contact_email',
        available: !!contact?.email,
      },
      {
        key: 'free_shipping',
        label: 'pricebook.shipping_cost',
        visible: true,
        mode: 'readonly',
        slot: 'free_shipping',
        available: !!context.hasFreeShipping,
      },
    ];
  }

  private defineReferenceFields(context: IOrderDetailFieldsContext): IDetailField[] {
    return [
      {
        key: 'customer_po_number',
        label: 'companies.customer_po_number',
        visible: true,
        mode: 'inline',
        inlineType: 'text',
        value: context.customerPoNumber || null,
        available: this.isOrderFieldVisible(context, 'customer_po_number'),
      },
      {
        key: 'quote_id',
        label: 'quotations_and_orders.quote_id',
        visible: true,
        mode: 'inline',
        inlineType: 'text',
        value: context.quoteId || null,
        available: this.isOrderFieldVisible(context, 'quote_id'),
      },
      {
        key: 'shipping_date',
        label: 'companies.shipping_date',
        visible: true,
        mode: 'inline',
        inlineType: 'date',
        // Shown localised, edited as the ISO day a native date input expects.
        value: this.formatDetailDate(context.shippingDate, context.currentLanguage),
        editValue: this.toDateInputValue(context.shippingDate),
        copyValue: null,
        available: this.isOrderFieldVisible(context, 'shipping_date'),
      },
      {
        key: 'internal_note',
        label: 'quotations_and_orders.internal_notes',
        visible: true,
        mode: 'inline',
        inlineType: 'textarea',
        value: context.comment || null,
        copyValue: null,
        available: this.isOrderFieldVisible(context, 'comment'),
      },
    ];
  }

  /**
   * Order-level custom fields, one inline-editable row each. Multi-selects have
   * no inline editor and stay read-only, reachable through the dialog.
   */
  private defineCustomFields(context: IOrderDetailFieldsContext): IDetailField[] {
    const definitions = context.objectCustomFields?.definitions ?? [];
    const values = (context.objectCustomFields?.values ?? {}) as Record<string, any>;

    const INLINE_BY_TYPE: Record<string, IDetailField['inlineType']> = {
      [CustomFieldTypes.TEXT]: 'text',
      [CustomFieldTypes.NUMBER]: 'text',
      [CustomFieldTypes.DECIMAL]: 'text',
      [CustomFieldTypes.URL]: 'text',
      [CustomFieldTypes.EMAIL]: 'text',
      [CustomFieldTypes.DATE]: 'date',
      [CustomFieldTypes.RICH_TEXT]: 'textarea',
      // A closed list of values and a yes/no are both single-choice pickers.
      [CustomFieldTypes.SINGLE_SELECT]: 'lookup',
      [CustomFieldTypes.BOOLEAN]: 'lookup',
    };

    return definitions.map((definition) => {
      const inlineType = INLINE_BY_TYPE[definition.field_type];
      const raw = values[definition.technical_name];
      const value = Array.isArray(raw) ? raw.join(', ') : raw ?? null;

      const field: IDetailField = {
        key: `order_custom_${definition.technical_name}`,
        label: this.customFieldLabel(definition, context.currentLanguage),
        visible: true,
        mode: inlineType ? 'inline' : 'readonly',
        inlineType,
        value,
      };

      if (inlineType === 'date') {
        field.value = this.formatDetailDate(value, context.currentLanguage);
        field.editValue = this.toDateInputValue(value);
      }

      if (inlineType === 'lookup') {
        const isBoolean = definition.field_type === CustomFieldTypes.BOOLEAN;
        const isUnset = raw === null || raw === undefined || raw === '';

        field.options = isBoolean
          ? [
              { id: 'true', name: this.translate.instant('common.yes') },
              { id: 'false', name: this.translate.instant('common.no') },
            ]
          : (definition.allowed_values ?? []).map((option) => ({ id: option, name: option }));
        field.selectedOptionId = isBoolean
          ? (isUnset ? null : String(!!raw))
          : (value as string | null);
        field.value = isBoolean
          ? (isUnset ? null : this.translate.instant(raw ? 'common.yes' : 'common.no'))
          : value;
        field.clearable = !definition.is_required;
        field.copyValue = null;
      }

      return field;
    });
  }

  /** Custom-field label in the active language, falling back to any locale. */
  private customFieldLabel(definition: ICustomField, language?: string): string {
    return (
      (language ? definition.label?.[language] : undefined) ||
      Object.values(definition.label ?? {}).find((label) => !!label) ||
      definition.technical_name
    );
  }
}
